// Sprint 6 — Tab "Versioni": timeline pubblicazioni + diff + rollback.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { FlowWithRelations } from '@audiq/shared';
import { api } from '../../lib/api';
import { ApiClientError } from '../../lib/api/_request';
import type { FlowVersionSummary, FlowVersionsDiff } from '../../lib/api/flows';

dayjs.extend(relativeTime);
dayjs.locale('it');

interface Props {
  flow: FlowWithRelations;
}

export function VersionsTab({ flow }: Props) {
  const qc = useQueryClient();
  const [diffOpen, setDiffOpen] = useState<{ a: string; b: string } | null>(null);

  const versionsQuery = useQuery({
    queryKey: ['flow-versions', flow.id],
    queryFn: () => api.listFlowVersions(flow.id),
  });

  const rollbackMut = useMutation({
    mutationFn: (versionId: string) => api.rollbackFlowToVersion(flow.id, versionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flow', flow.id] });
      qc.invalidateQueries({ queryKey: ['flow-versions', flow.id] });
    },
  });

  if (versionsQuery.isLoading) {
    return <p style={{ color: 'var(--mute)' }}>Carico versioni…</p>;
  }
  const versions = versionsQuery.data ?? [];

  if (versions.length === 0) {
    return (
      <div
        className="rounded-md border p-6 text-center"
        style={{ borderColor: 'var(--line)', background: 'var(--paper)', color: 'var(--mute)' }}
      >
        <p>Nessuna versione pubblicata.</p>
        <p className="mt-2 text-sm">
          Pubblica per la prima volta dal pulsante <strong>Pubblica</strong> in alto.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p style={{ color: 'var(--mute)' }} className="text-sm">
        Ogni pubblicazione genera uno snapshot immutabile. Le sessioni avviate dopo la pubblicazione
        usano l'ultima versione attiva.
      </p>
      <ul className="space-y-3">
        {versions.map((v, idx) => (
          <li
            key={v.id}
            className="rounded-md border p-4"
            style={{
              borderColor: v.is_active ? 'var(--primary)' : 'var(--line)',
              background: 'var(--paper)',
            }}
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="serif text-2xl" style={{ color: 'var(--ink)', lineHeight: 1 }}>
                  v{v.version_number}
                </span>
                {v.is_active && (
                  <span
                    className="mono rounded-full px-2 py-0.5"
                    style={{
                      background: 'color-mix(in oklab, var(--primary) 20%, transparent)',
                      color: 'var(--primary-2)',
                      fontSize: 11,
                    }}
                  >
                    attiva
                  </span>
                )}
              </div>
              <div className="mono text-right" style={{ fontSize: 11, color: 'var(--mute)' }}>
                {dayjs(v.published_at).format('DD MMM YYYY HH:mm')}
                {v.published_by_email && (
                  <>
                    <br />
                    {v.published_by_email}
                  </>
                )}
              </div>
            </div>
            {v.summary && (
              <div className="mono mt-2" style={{ fontSize: 12, color: 'var(--mute)' }}>
                {v.summary.questions_count} domand
                {v.summary.questions_count === 1 ? 'a' : 'e'} · {v.summary.outcomes_count} esit
                {v.summary.outcomes_count === 1 ? 'o' : 'i'} · lingua{' '}
                {v.summary.language_default.toUpperCase()}
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {idx < versions.length - 1 && (
                <button
                  type="button"
                  onClick={() => setDiffOpen({ a: versions[idx + 1]!.id, b: v.id })}
                  className="rounded-md px-2 py-1 text-sm"
                  style={{ background: 'var(--soft)', color: 'var(--ink)' }}
                >
                  Vedi diff vs v{versions[idx + 1]!.version_number}
                </button>
              )}
              {!v.is_active && (
                <button
                  type="button"
                  onClick={() => {
                    if (
                      confirm(
                        `Rollback a v${v.version_number}? Il draft attuale verrà sovrascritto. Le sessioni in corso continueranno con la versione attualmente attiva.`,
                      )
                    ) {
                      rollbackMut.mutate(v.id);
                    }
                  }}
                  disabled={rollbackMut.isPending}
                  className="rounded-md px-2 py-1 text-sm disabled:opacity-50"
                  style={{
                    background: 'var(--paper)',
                    border: '1px solid var(--line-strong)',
                    color: 'var(--ink)',
                  }}
                >
                  {rollbackMut.isPending ? 'Rollback…' : 'Rollback a questa versione'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {rollbackMut.isError && (
        <p style={{ color: 'var(--danger)' }} className="text-sm">
          {rollbackMut.error instanceof ApiClientError
            ? rollbackMut.error.payload.error
            : 'Errore durante il rollback.'}
        </p>
      )}

      {diffOpen && (
        <DiffModal
          flowId={flow.id}
          vAId={diffOpen.a}
          vBId={diffOpen.b}
          onClose={() => setDiffOpen(null)}
          versions={versions}
        />
      )}
    </div>
  );
}

interface DiffModalProps {
  flowId: string;
  vAId: string;
  vBId: string;
  versions: FlowVersionSummary[];
  onClose: () => void;
}

function DiffModal({ flowId, vAId, vBId, versions, onClose }: DiffModalProps) {
  const diffQuery = useQuery({
    queryKey: ['flow-diff', flowId, vAId, vBId],
    queryFn: () => api.getFlowVersionsDiff(flowId, vAId, vBId),
  });
  const vA = versions.find((v) => v.id === vAId);
  const vB = versions.find((v) => v.id === vBId);

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(31,36,33,0.45)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--paper)',
          borderRadius: 12,
          width: 'min(820px, 100%)',
          maxHeight: '85vh',
          overflow: 'auto',
          padding: 24,
          boxShadow: '0 8px 32px rgba(31,36,33,0.18)',
        }}
      >
        <div className="flex items-baseline justify-between">
          <h3 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
            Diff v{vA?.version_number} → v{vB?.version_number}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-2xl"
            style={{ color: 'var(--mute)', lineHeight: 1 }}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        {diffQuery.isLoading && <p style={{ color: 'var(--mute)' }}>Carico…</p>}
        {diffQuery.data && <DiffBody diff={diffQuery.data} />}
      </div>
    </div>
  );
}

function DiffBody({ diff }: { diff: FlowVersionsDiff }) {
  const sections: { title: string; render: () => JSX.Element | null }[] = [
    {
      title: 'Persona',
      render: () =>
        diff.persona.changed_keys.length === 0 ? null : (
          <p style={{ color: 'var(--ink)' }}>
            Campi modificati: <strong>{diff.persona.changed_keys.join(', ')}</strong>
          </p>
        ),
    },
    {
      title: 'Settings',
      render: () =>
        diff.settings.changed_keys.length === 0 ? null : (
          <p style={{ color: 'var(--ink)' }}>
            Campi modificati: <strong>{diff.settings.changed_keys.join(', ')}</strong>
          </p>
        ),
    },
    {
      title: 'Boundaries',
      render: () =>
        diff.boundaries.added.length === 0 && diff.boundaries.removed.length === 0 ? null : (
          <div>
            {diff.boundaries.added.length > 0 && (
              <p style={{ color: 'var(--primary-2)' }}>
                + {diff.boundaries.added.length} aggiunt
                {diff.boundaries.added.length === 1 ? 'a' : 'e'}
              </p>
            )}
            {diff.boundaries.removed.length > 0 && (
              <p style={{ color: 'var(--danger)' }}>
                − {diff.boundaries.removed.length} rimoss
                {diff.boundaries.removed.length === 1 ? 'a' : 'e'}
              </p>
            )}
          </div>
        ),
    },
    {
      title: 'Lingua default',
      render: () =>
        diff.language_default ? (
          <p style={{ color: 'var(--ink)' }}>
            <strong>{diff.language_default.from}</strong> →{' '}
            <strong>{diff.language_default.to}</strong>
          </p>
        ) : null,
    },
    {
      title: 'Domande',
      render: () => {
        const { added, removed, modified } = diff.questions;
        if (added.length === 0 && removed.length === 0 && modified.length === 0) return null;
        return (
          <div>
            {added.length > 0 && (
              <div style={{ color: 'var(--primary-2)', fontSize: 13 }}>
                + Aggiunte:
                <ul className="ms-4 list-disc">
                  {added.map((q) => (
                    <li key={q.id}>{q.objective}</li>
                  ))}
                </ul>
              </div>
            )}
            {removed.length > 0 && (
              <div style={{ color: 'var(--danger)', fontSize: 13 }}>
                − Rimosse:
                <ul className="ms-4 list-disc">
                  {removed.map((q) => (
                    <li key={q.id}>{q.objective}</li>
                  ))}
                </ul>
              </div>
            )}
            {modified.length > 0 && (
              <div style={{ color: 'var(--accent-2)', fontSize: 13 }}>
                ≈ Modificate:
                <ul className="ms-4 list-disc">
                  {modified.map((q) => (
                    <li key={q.id}>
                      {q.objective}{' '}
                      <span style={{ color: 'var(--mute)' }}>({q.changed_fields.join(', ')})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Esiti',
      render: () => {
        const { added, removed, modified } = diff.outcomes;
        if (added.length === 0 && removed.length === 0 && modified.length === 0) return null;
        return (
          <div>
            {added.length > 0 && (
              <div style={{ color: 'var(--primary-2)', fontSize: 13 }}>
                + Aggiunti:
                <ul className="ms-4 list-disc">
                  {added.map((o) => (
                    <li key={o.id}>{o.name}</li>
                  ))}
                </ul>
              </div>
            )}
            {removed.length > 0 && (
              <div style={{ color: 'var(--danger)', fontSize: 13 }}>
                − Rimossi:
                <ul className="ms-4 list-disc">
                  {removed.map((o) => (
                    <li key={o.id}>{o.name}</li>
                  ))}
                </ul>
              </div>
            )}
            {modified.length > 0 && (
              <div style={{ color: 'var(--accent-2)', fontSize: 13 }}>
                ≈ Modificati:
                <ul className="ms-4 list-disc">
                  {modified.map((o) => (
                    <li key={o.id}>
                      {o.name}{' '}
                      <span style={{ color: 'var(--mute)' }}>({o.changed_fields.join(', ')})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const visible = sections.filter((s) => s.render() !== null);
  if (visible.length === 0) {
    return (
      <p className="mt-4" style={{ color: 'var(--mute)' }}>
        Nessuna differenza fra le due versioni.
      </p>
    );
  }
  return (
    <div className="mt-4 space-y-4">
      {visible.map((s) => (
        <section key={s.title}>
          <h4
            className="mono mb-1"
            style={{ color: 'var(--mute)', fontSize: 11, textTransform: 'uppercase' }}
          >
            {s.title}
          </h4>
          {s.render()}
        </section>
      ))}
    </div>
  );
}
